import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Toaster, toast } from 'react-hot-toast';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Spinner,
  Text
} from "@fluentui/react-components";
import LoginPage from "./LoginPage";
import ChatInterface from "./ChatInterface";
import UploadModal from "./UploadModal";
import ExistingFilesModal from "./ExistingFilesModal";
import { useInactivityTimer } from "../utils/useInactivityTimer.jsx";
import "./StyleSheet.css";
import botImg from "../assets/pen-hand-icon.png";
import logger, { updateLoggerContext } from "../utils/logger.jsx";
import { fetchWithIap } from '../utils/iapService';
import NewWindowPortal from "./NewWindowPortal.jsx";
import PromptLibrary from "./PromptLibrary.jsx";
import ConfirmationModal from "./ConfirmationModal";
import PermissionModal from "./PermissionModal";
/**
 * Helper function to convert a FormData instance into a plain JavaScript object.
 * Useful for logging out form payload structures.
 * 
 * @param {FormData} formData - The FormData object to convert.
 * @returns {Object} A plain JavaScript object representing the form data.
 */
const formDataToObject = (formData) => {
  const obj = {};
  for (const [key, value] of formData.entries()) {
    obj[key] = value;
  }
  return obj;
};
const initialMessages = [{ sender: "bot", type: "welcome", text: "Hello I am AIScribe, How can I assist you today?", avatar: botImg }];

//CENTRALIZED API ENDPOINTS
const API_PROCESS_ENDPOINT = "api/process";
const API_LOGIN_ENDPOINT = "api/login";
const API_LOGOUT_ENDPOINT = "api/logout";
const API_LIST_FILES_ENDPOINT = "api/list_files";
const API_DELETE_FILE_ENDPOINT = "api/delete_file";
const API_GENERATE_URLS_ENDPOINT = "api/generate_signed_urls";
const API_PROCESS_GCS_ENDPOINT = "api/process_gcs_files";
const API_FEEDBACK_ENDPOINT = "api/response_rating";
const API_RESPONSE_COPY_ENDPOINT = "api/response_copy";
const API_ADD_BOOKMARK_ENDPOINT = "api/bookmark";
const API_REMOVE_BOOKMARKS_ENDPOINT = "api/remove_bookmarks";
const API_STOP_GENERATION_ENDPOINT = "api/stop_generation";

/**
 * Main Application Component for AIScribe.
 * Handles the state, routing, and primary business logic for chat, uploads, and authentication.
 * 
 * @param {Object} props - The component props.
 * @param {string} props.title - The application title.
 * @param {Function} props.onLoginRequest - Callback triggered to initiate login.
 * @param {string} props.initialProxySessionId - The Proxy Session id provided on initialization.
 * @param {boolean} props.isAuthenticating - Boolean representing if auth is currently processing.
 * @param {string} props.authError - Any error string generated during authentication.
 */
const App = (props) => {
  const { title, onLoginRequest, initialProxySessionId, isAuthenticating, authError } = props;
  //Application State
  const [activeButton, setActiveButton] = useState(null);
  const [messages, setMessages] = useState(initialMessages);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [user, setUser] = useState(null);
  const [authIsLoading, setAuthIsLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars    
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isExistingFilesModalOpen, setIsExistingFilesModalOpen] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]);
  const [isCheckingFiles, setIsCheckingFiles] = useState(false);
  const [isDeletingFiles, setIsDeletingFiles] = useState(false);
  const [isPromptLibraryOpen, setIsPromptLibraryOpen] = useState(false);
  const [promptLibraryRefreshKey, setPromptLibraryRefreshKey] = useState(0);
  const [toasterKey, setToasterKey] = useState(1);
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);
  const [inactivityTimeout, setInactivityTimeout] = useState(3300000);
  const [showRevaluationWarning, setShowRevaluationWarning] = useState(false);
  const [docLibFilters, setDocLibFilters] = useState({ docTypes: [], customerAreas: [] });
  const [isGroundingEnabled, setIsGroundingEnabled] = useState(false);
  const [uploadQuotaData, setUploadQuotaData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const abortControllerRef = useRef(null);
  const [maxTokenLimit, setMaxTokenLimit] = useState(null);
  const [totalToken, setTotalToken] = useState(null);
  const [isStopping, setIsStopping] = useState(false);
  const [failedFilesErrors, setFailedFilesErrors] = useState([]);
  const [isFailedFilesModalOpen, setIsFailedFilesModalOpen] = useState(false);
  const [romineRequiredRole, setRomineRequiredRole] = useState('GLOAZUaiscribe-developers');
  const [docLibRequiredRoles, setDocLibRequiredRoles] = useState(["GLOAZUaiscribe-developers",]);  
  const [permissionModal, setPermissionModal] = useState({
		isOpen: false,
		featureName: "",
	});  

  /**
    * Opens the confirmation modal to verify if the user wants to clear the chat.
    */
  const handleOpenClearChatModal = () => {
    if (isLoading) return;
    setModalConfig({
      title: "Confirm Clear Chat",
      message: "Are you sure you want to clear the current chat session? This will remove all messages and cannot be undone.",
      confirmButtonText: "Yes, Clear Chat",
      // Store the function to be executed on confirm
      onConfirmAction: () => handleClearChat(),
    });
  };

  /**
    * Opens the confirmation modal to verify if the user wants to log out.
    */
  const handleOpenLogoutModal = () => {
    if (isLoading) return;
    setModalConfig({
      title: "Confirm Logout",
      message: "Are you sure you want to log out? You will need to sign in again.",
      confirmButtonText: "Yes, Logout",
      onConfirmAction: () => handleLogout(false), // Pass `false` for manual logout
    });
  };

  /**
  * Effect hook to perform backend login automatically once an IAP session is available.
  * Runs whenever the `initialProxySessionId` changes.
  */
  useEffect(() => {
    // eslint-disable-next-line no-unused-vars  
    const performBackendLogin = async (proxySessionId) => {
      const apiTag = `POST ${API_LOGIN_ENDPOINT}`;
      logger.info('IAP Proxy SessionId received, attempting backend login.', { functionName: 'performBackendLogin', api: apiTag });
      setAuthIsLoading(true);

      try {
        const response = await fetchWithIap(API_LOGIN_ENDPOINT, {
          method: "POST",
          headers: {
            "X-Session-ID": sessionId,
            "Content-Type": "application/json",
            "Client-Type": 'Word'
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          logger.error('Backend login API returned an error.', { status: response.status, errorPayload: errorData });
          throw new Error(errorData.detail || `Backend login failed: ${response.status}`);
        }

        const loginResponse = await response.json();
        logger.info('Backend login successful.', { responsePayload: loginResponse });

        const loggedInUser = {
          displayName: loginResponse.full_name,
          email: loginResponse.user_email,
          roles: loginResponse.roles || [],
        };

        const personalizedWelcomeMessage = {
          sender: "bot", type: "welcome",
          text: `Hello ${loggedInUser.displayName}, I am AIScribe. How can I assist you today?`,
          avatar: botImg
        };

        setUser(loggedInUser);
        setMessages([personalizedWelcomeMessage]);
        toast.success("Login Successful!");

      } catch (err) {
        console.error("Error during backend login:", err);
        logger.error('Backend login process failed.', { originalError: err });
        setError(err.message);
        toast.error(`${err.message}`);
      } finally {
        setAuthIsLoading(false);
      }
    };

    if (initialProxySessionId) {
      performBackendLogin(initialProxySessionId);
    } else {
      setAuthIsLoading(false);
    }
  }, [initialProxySessionId]);

  /**
 * Effect hook to listen for events from the IAP service to show a "refreshing" overlay.
 * Binds 'iap-refresh-start' and 'iap-refresh-end' to window event listeners.
 */
  useEffect(() => {
    const handleRefreshStart = () => setIsRefreshingSession(true);
    const handleRefreshEnd = () => setIsRefreshingSession(false);

    window.addEventListener('iap-refresh-start', handleRefreshStart);
    window.addEventListener('iap-refresh-end', handleRefreshEnd);

    return () => {
      window.removeEventListener('iap-refresh-start', handleRefreshStart);
      window.removeEventListener('iap-refresh-end', handleRefreshEnd);
    };
  }, []);


  /**
 * Memoized hook to calculate the total size of all existing uploaded files.
 * Recalculates only when the `existingFiles` state array changes.
 */
  const totalExistingFileSize = React.useMemo(() => {
    return existingFiles.reduce((total, file) => total + (file.size || 0), 0);
  }, [existingFiles]);

  /**
 * Memoized hook to map and extract just the filenames from the existing files list.
 */
  const existingFileNames = React.useMemo(
    () => existingFiles.map(file => file.name),
    [existingFiles]
  );

  // This special view is rendered only if the app is opened in the IAP session refresh popup window.
  const urlParams = new URLSearchParams(window.location.search);
  const isRefreshPopup = urlParams.get('gcp-iap-mode') === 'DO_SESSION_REFRESH';
  if (isRefreshPopup) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
        fontFamily: 'Segoe UI, sans-serif' // Matches typical Word add-in font
      }}>
        {/* Reuse the Fluent UI Spinner you already imported */}
        <Spinner size="large" label="Refreshing Session..." />
        <div style={{ marginTop: '20px', color: '#555' }}>
          <h3>Authentication in progress</h3>
          <p>Please wait while we refresh your security session.</p>
          <p style={{ fontSize: '0.9em', color: '#888' }}>
            This window will close automatically once completed.
          </p>
        </div>
      </div>
    );
  }

  /**
  * Effect hook to update the logger context whenever the authenticated user or session ID changes.
  */
  useEffect(() => {
    setAuthIsLoading(false);
    updateLoggerContext({
      sessionId: sessionId,
      userId: user ? user.email : 'ANONYMOUS',
      userDisplayName: user ? user.displayName : 'N/A',
    });
    if (user) {
      logger.info('Logger context updated for signed-in user.');
    }
  }, [user, sessionId]);

  /**
 * Effect hook to fetch the inactivity timeout limit from the backend configuration.
 * Runs once on mount (or when `user` changes) and overrides the default timeout.
 */
  useEffect(() => {
    if (!user) return;  
    // We fetch the config on mount, just like UploadModal fetches on open
    fetch('/api/config', { credentials: 'include' })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        // Check for "inactivityTimeout"
        // or "inactivityTimeout"
        const timeoutVal = data.inactivityTimeout || data.inactivity_timeout;

        if (timeoutVal) {
          const parsed = parseInt(timeoutVal, 10);
          if (!isNaN(parsed)) {
            setInactivityTimeout(parsed);
            logger.info(`Inactivity timeout set from API config: ${parsed}ms`);
          }
        }
        // Catch the role from the config response
        if (data.romineRequiredRole) {
          setRomineRequiredRole(data.romineRequiredRole);
          logger.info(`RoMine required role set from API config: ${data.romineRequiredRole}`);
        }
        // handle DocLib configuration parsing:
		if (data.doclibRequiredRoles || data.doclibRequiredRole) {
			const rawDocLibRoles =	data.doclibRequiredRoles || data.doclibRequiredRole;

			const parsedDocLibRoles = Array.isArray(rawDocLibRoles)
						? rawDocLibRoles
						: rawDocLibRoles
								.split(",")
								.map((r) => r.trim())
								.filter(Boolean);

			setDocLibRequiredRoles(parsedDocLibRoles);
			logger.info(`DocLib required roles set from API config: ${parsedDocLibRoles.join(", ")}`,);
		}
      })
      .catch(error => {
        console.warn("Failed to fetch inactivity configuration. Using default.", error);
      });
  }, [user]);

  /**
* Closes the standard file upload modal. 
* If closing after a quota recovery, it automatically transitions the source mode to 'file_upload'.
*/
  const handleCloseUploadModal = () => {
    if (uploadQuotaData) {
      handleSourceChange("file_upload");
      toast.success("Ready! You can now ask questions about your uploaded files.");
    }
    setIsUploadModalOpen(false);
    setUploadError("");
    setUploadQuotaData(null);
  };

  /**
  * Closes the "Existing Files" management modal.
  */
  const handleCloseExistingFilesModal = () => {
    setIsExistingFilesModalOpen(false);
  };

  /**
 * Handles the UI flow of switching from the upload modal to the existing files modal,
 * typically invoked when managing files after exceeding a quota.
 */
  const handleManageExistingFiles = () => {
    setIsUploadModalOpen(false);
    setIsExistingFilesModalOpen(true);
  };

  /**
 * Handles clicking "Upload New Files" from the existing files modal.
 * Transitions the user to the standard upload interface.
 */
  const handleProceedToUpload = () => {
    handleCloseExistingFilesModal();
    setIsUploadModalOpen(true);
  };

  /**
* Toggles the selection (`isSelected`) state of a single specific file.
* 
* @param {string} fileName - The name of the file to toggle.
*/
  const handleToggleFileSelection = (fileName) => {
    // Dismiss warning if user modifies selection
    if (showRevaluationWarning) setShowRevaluationWarning(false);
    setExistingFiles(prevFiles =>
      prevFiles.map(file =>
        file.name === fileName ? { ...file, isSelected: !file.isSelected } : file
      )
    );
  };

  /**
 * Toggles the selection state for all files at once.
 * 
 * @param {boolean} shouldSelect - Boolean indicating if all should be selected or deselected.
 */
  const handleSelectAllFiles = (shouldSelect) => {
    // Dismiss warning if user modifies selection
    if (showRevaluationWarning) setShowRevaluationWarning(false);
    setExistingFiles(prevFiles =>
      prevFiles.map(file => ({ ...file, isSelected: shouldSelect }))
    );
  };

  /**
 * Dismisses the banner warning shown during file selection revaluation.
 */
  const handleDismissRevaluationWarning = () => {
    setShowRevaluationWarning(false);
  };

  /**
* General handler to open the reusable confirmation modal.
*/
    // eslint-disable-next-line no-unused-vars  
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  /**
* General handler to close the reusable confirmation modal and clear configurations.
*/
  const handleCloseModal = () => {
    setModalConfig(null);
    setIsModalOpen(false);
    setUploadError(""); // Clear any previous errors on close
  };

  /**
 * Executes the action configured within the currently open confirmation modal.
 */
  const handleConfirmModal = async () => {
    if (modalConfig && modalConfig.onConfirmAction) {
      await modalConfig.onConfirmAction();
    }
    setModalConfig(null);
  };


/**
	 * Changes the active data source button (e.g., 'file_upload', 'DocLib', 'RoMine').
	 * Also acts as the security layer checking for correct user roles.
	 */
	const handleSourceChange = (sourceName) => {
		// Check if user has AT LEAST one matching role
		// Access control check for DocLib
		const hasDocLibAccess = user?.roles?.some((role) =>
			docLibRequiredRoles.includes(role),
		);
		if (sourceName === "DocLib" && !hasDocLibAccess) {
			setPermissionModal({ isOpen: true, featureName: "DocLib" });
			return;
		}

		setActiveButton(sourceName);
		setError("");
	};

  /**
     * Fetches the authoritative list of already uploaded files directly from the server.
     * Also populates app-level token tracking values.
     * 
     * @returns {Promise<Array>} An array of formatted file objects retrieved from the server.
     */
  const handleGetFileList = async () => {
    const apiTag = `GET ${API_LIST_FILES_ENDPOINT}`;
    logger.info('Fetching file list.', { functionName: 'handleGetFileList', api: apiTag });

    setIsCheckingFiles(true);
    try {
      const response = await fetchWithIap(API_LIST_FILES_ENDPOINT, {
        method: "GET",
        headers: {
          "X-Session-ID": sessionId,
          "Content-Type": "application/json",
          'Client-Type': 'Word',
        },
      });

      if (!response.ok) {
        const errorJson = await response.json().catch(() => ({}));
        logger.error('Failed to get file list.', { functionName: 'handleGetFileList', api: apiTag, status: response.status, errorPayload: errorJson });
        throw new Error(errorJson.error || `Failed to get list of uploaded files.`);
      }

      const jsonResponse = await response.json();

      if (jsonResponse.max_limit !== undefined) {
        setMaxTokenLimit(jsonResponse.max_limit);
      }
      if (jsonResponse.total_tokens !== undefined) {
        setTotalToken(jsonResponse.total_tokens);
      }
      console.log("maxtoken limit:", maxTokenLimit)
      // Backend parameter change to this all_session_files array to get tokens
      const sessionFiles = jsonResponse.all_session_files || [];
      const userFiles = sessionFiles.map(file => ({
        name: file.filename,
        tokens: file.tokens
      }));

      logger.info('Successfully fetched file list.', { functionName: 'handleGetFileList', api: apiTag, responsePayload: jsonResponse });
      console.info('Successfully fetched file list:', userFiles);
      return userFiles;

    } catch (error) {
      console.error('Error when getting list of uploaded files.', error);
      toast.error(error.message || "Oops! Something went wrong while accessing the list of files.", { duration: 6000, containerId: 'aiscribe-main' });
      return [];
    } finally {
      setIsCheckingFiles(false);
    }
  };

  /**
 * The master orchestrator flow for handling a click on the "Upload" menu button.
 * Fetches current files to determine whether to show the "Existing Files" modal or the standard upload modal.
 */
  const handleUploadClickFlow = async () => {
    const userFiles = await handleGetFileList();

    if (userFiles && userFiles.length > 0) {
      handleSourceChange("file_upload");

      // Preserve existing selections when refreshing the file list.
      const currentSelections = new Map(
        existingFiles.filter(f => f.isSelected).map(f => [f.name, true])
      );
      const filesForState = userFiles.map(file => ({
        name: file.name,
        size: file.size,
        tokens: file.tokens,
        isSelected: currentSelections.has(file.name) || false
      }));

      // If files exist, transform for the modal state and show the "Existing Files" modal.
      setExistingFiles(filesForState);
      setIsExistingFilesModalOpen(true);
    } else {
      // If no files exist, or if the API call failed, open the regular upload modal.
      setIsUploadModalOpen(true);
    }
  };

  /**
   * Handles the actual deletion of files by calling the backend API.
   * Replaces handleMockDeleteFiles.
   */
  const handleRealDeleteFiles = async (fileNamesToDelete) => {
    const apiTag = `POST ${API_DELETE_FILE_ENDPOINT}`;
    const requestPayload = { filenames: fileNamesToDelete };

    logger.info('Attempting to delete files.', { functionName: 'handleRealDeleteFiles', api: apiTag, requestPayload });

    const response = await fetchWithIap(API_DELETE_FILE_ENDPOINT, {
      method: "POST",
      headers: {
        "X-Session-ID": sessionId,
        "Content-Type": "application/json",
        'Client-Type': 'Word',
      },
      body: JSON.stringify(requestPayload)
    });

    if (!response.ok) {
      const errorJson = await response.json().catch(() => ({}));
      logger.error('Delete files API returned an error.', { functionName: 'handleRealDeleteFiles', api: apiTag, status: response.status, errorPayload: errorJson });
      throw new Error(errorJson.error || "Failed to delete files. Please try again.");
    }

    const jsonResponse = await response.json();
    // Use the new all_session_files array just like in list_files
    const sessionFiles = jsonResponse.all_session_files || [];

    if (jsonResponse.total_tokens) {
      setTotalToken(jsonResponse.total_tokens);
    }
    const remainingFiles = sessionFiles.map(file => ({
      name: file.filename,
      tokens: file.tokens, // Keep the tokens in state!
      isSelected: false
    }));


    logger.info('Successfully deleted files.', { functionName: 'handleRealDeleteFiles', api: apiTag, responsePayload: jsonResponse });
    console.info('Successfully deleted files. Remaining:', remainingFiles);

    return remainingFiles; // Return just the names
  };

  /**
  * Deletes a single existing file, primarily utilized directly from the recovery Upload Modal.
  * 
  * @param {string} fileName - The specific name of the file to delete.
  */
  const handleDeleteSingleExistingFile = async (fileName) => {
    try {
      // Use your existing backend delete function
      const remainingFiles = await handleRealDeleteFiles([fileName]);

      // Update the existing files list
      setExistingFiles(remainingFiles);

      if (remainingFiles.length === 0) {
        logger.info('Last file deleted during quota recovery. Resetting modal state.');
        setUploadQuotaData(null);
        // Provide clear feedback to the user.
        toast.info("All files cleared. You can now upload a new set.", { id: 'last-file-deleted' });

      } else {
        // If there are still files left, just update the token data for the modal's counter.
        // This is the existing logic that works for multi-file scenarios.
        // Update the quota data dynamically so the Red Box updates instantly!
        setUploadQuotaData(prev => {
          if (!prev) return null;
          const newFileTokens = { ...prev.fileTokens };
          delete newFileTokens[fileName]; // Remove the deleted file's tokens
          return { ...prev, fileTokens: newFileTokens };
        });

        toast.success(`Deleted ${fileName} to free up space.`);
      }
    } catch (error) {
      console.error("Failed to delete file:", error);
      toast.error(error.message || `Failed to delete ${fileName}.`);
    }
  };

  /**
   * Orchestrator function to delete selected files.
   * This now calls the real delete handler.
   */
  const handleUnifiedDelete = async () => {
    const filesToDelete = existingFiles.filter(file => file.isSelected).map(file => file.name);

    if (filesToDelete.length === 0) {
      toast.info("No files are selected to delete.");
      return;
    }

    logger.info('User initiated deletion of selected files.', {
      functionName: 'handleUnifiedDelete',
      fileCount: filesToDelete.length,
      files: filesToDelete
    });

    setIsDeletingFiles(true);
    try {
      const remainingFiles = await handleRealDeleteFiles(filesToDelete);

      setExistingFiles(remainingFiles);
      toast.success(`Successfully deleted ${filesToDelete.length} file(s).`);

      if (remainingFiles.length === 0) {
        logger.info('All user files have been removed as part of deletion flow.', { functionName: 'handleUnifiedDelete' });

        setActiveButton(null);

        handleCloseExistingFilesModal();
        // toast.info("All files have been removed. You can now start a new upload.");

        const allClearedMessage = {
          sender: 'bot',
          type: 'file-list',
          isCleared: true,
          avatar: botImg
        };
        setMessages(prev => [...prev, allClearedMessage]);
      }
    } catch (error) {
      console.error("Deletion failed:", error);
      logger.error('The overall file deletion process failed.', { functionName: 'handleUnifiedDelete', originalError: error });
      toast.error(error.message);
    } finally {
      setIsDeletingFiles(false);
    }
  };


  /**
   * Retrieves a batch of Google Cloud Storage (GCS) signed URLs for securely uploading files directly.
   * 
   * @param {File[]} filesToUpload - Array of native File objects preparing for upload.
   * @returns {Promise<Array>} Array of signed URL data objects containing target endpoints.
   */
  const getGcsSignedUrls = async (filesToUpload) => {
    const apiTag = `POST ${API_GENERATE_URLS_ENDPOINT}`;
    const requestPayload = {
      files: filesToUpload.map(file => file.name),
      expiration_minutes: 60
    };
    logger.info('Requesting GCS signed URLs.', { functionName: 'getGcsSignedUrls', api: apiTag, requestPayload });

    const response = await fetchWithIap(API_GENERATE_URLS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-ID': sessionId,
        'Client-Type': 'Word'
      },
      body: JSON.stringify(requestPayload)
    });

    if (!response.ok) {
      const responseBody = await response.json().catch(() => ({}));
      logger.error('Failed to generate GCS signed URLs.', { functionName: 'getGcsSignedUrls', api: apiTag, status: response.status, errorPayload: responseBody });

      const errorMsg = (responseBody && responseBody.error) ? responseBody.error : 'Failed to generate GCS signed URLs';
      throw new Error(errorMsg);
    }

    const jsonResponse = await response.json();
    console.info('Successfully generated signed URLs.', jsonResponse);
    logger.info('Successfully generated GCS signed URLs.', {
      functionName: 'getGcsSignedUrls',
      api: apiTag,
      urlCount: jsonResponse.signed_urls ? jsonResponse.signed_urls.length : 0
    });
    return jsonResponse.signed_urls;
  };

  /**
* Silently deletes a file from the backend in the background.
* Useful to prevent ghost files from lingering during errors or aborted quota scenarios.
* 
* @param {string} fileName - The specific filename to remove.
*/
  // eslint-disable-next-line no-unused-vars    
  const handleBackgroundFileCleanup = async (fileName) => {
    try {
      await fetchWithIap(API_DELETE_FILE_ENDPOINT, {
        method: "POST",
        headers: {
          "X-Session-ID": sessionId,
          "Content-Type": "application/json",
          'Client-Type': 'Word',
        },
        body: JSON.stringify({ filenames: [fileName] })
      });
      console.log(`[Cleanup] Removed ${fileName} from backend to free up quota.`);
    } catch  {
      // Silently ignore. If the file wasn't on the backend yet, it doesn't matter.
      console.log(`[Cleanup] No backend action needed for ${fileName}.`);
    }
  };

  /**
 * The master pipeline handling the entire upload process.
 * 1. Get GCS Signed URLs
 * 2. Upload to GCS directly and concurrently
 * 3. Notify backend to process and tokenize the files
 * 
 * @param {File[]} filesToUpload - Collection of physical File objects chosen by the user.
 */
  const handleRealFileUpload = async (filesToUpload) => {
    // Check if files exist BEFORE this upload processes
    const isSubsequentUpload = existingFiles.length > 0;
    // check if there is currently a file selected/checked?
    const hasSelectedFiles = existingFiles.some(file => file.isSelected);
    console.log("hasSelectedFiles", hasSelectedFiles);

    const fileMetadata = filesToUpload.map(f => ({ name: f.name, size: f.size, type: f.type }));
    logger.info('Starting file upload process.', { functionName: 'handleRealFileUpload', fileCount: filesToUpload.length, files: fileMetadata });

    setIsUploading(true);
    setUploadError("");

    try {
      // Get all signed URLs 
      const signedUrlsData = await getGcsSignedUrls(filesToUpload);

      logger.info('Starting parallel uploads to GCS.', { functionName: 'handleRealFileUpload', urlCount: signedUrlsData.length });

      const fileMap = new Map(filesToUpload.map(file => [file.name, file]));

      const gcsUploadPromises = [];

      // Upload files to GCS in parallel
      for (const urlData of signedUrlsData) {
        const file = fileMap.get(urlData.filename);
        if (!file) {
          logger.warn('No matching file found for a signed URL.', { functionName: 'handleRealFileUpload', filename: urlData.filename });
          console.warn(`Could not find a matching file for signed URL: ${urlData.filename}. Skipping.`);
          continue;
        }

        const gcsUploadPromise = fetch(urlData.signed_url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file
        }).then(async (gcsResponse) => {
          if (!gcsResponse.ok) {
            const errorBody = await gcsResponse.text().catch(() => 'Could not read GCS error body.');

            logger.error('A single file upload to GCS failed.', { functionName: 'handleRealFileUpload', file: file.name, status: gcsResponse.status, errorBody });
            console.error('Failed to upload file to GCS.', { file: file.name, status: gcsResponse.status, body: errorBody });

            const specificError = `Upload failed for ${file.name}. Reason: ${errorBody}`;
            throw new Error(specificError);
          }
          return gcsResponse;
        });
        gcsUploadPromises.push(gcsUploadPromise);
      }
      await Promise.all(gcsUploadPromises);

      logger.info('All files successfully uploaded to GCS. Notifying backend.', { functionName: 'handleRealFileUpload' });
      console.info('All files successfully uploaded to GCS. Notifying backend.');


      const apiTag = "POST /process_gcs_files"

      //Notify backend to process files
      const finalProcessResponse = await fetchWithIap(API_PROCESS_GCS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'Client-Type': 'Word'
        },
      });

      if (!finalProcessResponse.ok) {
        const finalResponseBody = await finalProcessResponse.json().catch(() => ({}));
        logger.error('Backend notification for file processing failed.', { functionName: 'handleRealFileUpload', api: apiTag, status: finalProcessResponse.status, errorPayload: finalResponseBody });

        const errorMsg = (finalResponseBody && finalResponseBody.error) ? finalResponseBody.error : 'Backend file processing notification failed.';
        throw new Error(errorMsg);
      }


      const jsonResponse = await finalProcessResponse.json();
      logger.info('Backend acknowledged file processing.', { functionName: 'handleRealFileUpload', api: apiTag, responsePayload: jsonResponse });


      if (jsonResponse.is_limit_exceeded) {
        const tokenMap = {};
        let newTotalTokens = 0; // Track total
        if (jsonResponse.all_session_files) {
          const filesForState = jsonResponse.all_session_files.map(f => {
            tokenMap[f.filename] = f.tokens;
            newTotalTokens += (f.tokens || 0); // Add up tokens
            return { name: f.filename, tokens: f.tokens, isSelected: false };
          });
          // Update the background files so the Manage Existing Files modal is accurate!
          setExistingFiles(filesForState);
        }

        // Update global tokens
        setTotalToken(jsonResponse.total_tokens || newTotalTokens);
        if (jsonResponse.max_limit) setMaxTokenLimit(jsonResponse.max_limit);

        setUploadQuotaData({
          maxLimit: jsonResponse.max_limit,
          fileTokens: tokenMap
        });

        throw new Error("QUOTA_EXCEEDED");
      } else {
        setUploadQuotaData(null);
      }

      //Sync app state with the definitive list of files from the backend.
      const updatedFiles = await handleGetFileList();
      if (updatedFiles && updatedFiles.length > 0) {
        const currentSelections = new Map(
          existingFiles.filter(f => f.isSelected).map(f => [f.name, true])
        );
        const filesForState = updatedFiles.map(file => ({
          name: file.name,
          tokens: file.tokens,
          isSelected: currentSelections.has(file.name) || false
        }));
        setExistingFiles(filesForState);
        console.log('[handleRealFileUpload] State updated with merged file list:', filesForState);
      }


        
        if (jsonResponse.failed_files && jsonResponse.failed_files.length > 0) {
            const failedFiles = jsonResponse.failed_files;

            logger.warn('Backend reported partial failures during file processing.', { functionName: 'handleRealFileUpload', failedFiles });
            console.warn('Some files failed backend processing.', failedFiles);

            // If 1 or 2 files failed, show individual toaster messages
            if (failedFiles.length <= 2) {
             failedFiles.forEach(fileInfo => {
              let errorMessage = fileInfo.error || "Failed to process file.";

              // Optionally add the filename to the toast if your backend provides it and it isn't already in the error string
              const fileName = fileInfo.filename || fileInfo.name || fileInfo.file;
              if (fileName && !errorMessage.includes(fileName)) {
               errorMessage = `${fileName}: ${errorMessage}`;
              }

              toast.error(errorMessage);
             });
            } 
            // If 3 or more files failed, open the summary modal
            else {
             setFailedFilesErrors(failedFiles);
             setIsFailedFilesModalOpen(true);
            }
       }

      const wasAnyFileSuccessful = (jsonResponse.processed_files && jsonResponse.processed_files.length > 0)
      const wasTotalSuccess = (!jsonResponse.failed_files || jsonResponse.failed_files.length === 0);

      if (wasAnyFileSuccessful || wasTotalSuccess) {
        const successfulCount = jsonResponse.processed_files?.length || 0;
        if (successfulCount > 0 && wasTotalSuccess) {
          toast.success(`${successfulCount} file(s) uploaded successfully!`);
        }
        logger.info('File upload process concluded with at least one successful file.', { functionName: 'handleRealFileUpload' });
          // eslint-disable-next-line no-unused-vars   
        const updatedFiles = await handleGetFileList();
        handleSourceChange("file_upload");
        handleCloseUploadModal();
        // Only show warning if this was NOT the first upload and user had files selected previously
        if (isSubsequentUpload && hasSelectedFiles) {
          setShowRevaluationWarning(true);
        } else {
          // Ensure it's off otherwise
          setShowRevaluationWarning(false);
        }
      } else {
        setUploadError("All selected files failed to process. Please check the errors and try again.");
        logger.error('All files sent for processing failed on the backend.', { functionName: 'handleRealFileUpload', failedFiles: jsonResponse.failed_files });
      }

    } catch (e) {
      console.error("Overall file upload and processing failed:", e);
      logger.error('The overall file upload process failed critically.', {
        functionName: 'handleRealFileUpload',
        originalError: e
      });
      const errorMessageString = e.message || "An unexpected error occurred during upload.";
      try {
        const parsedJson = JSON.parse(errorMessageString);
        setUploadError(parsedJson.error || errorMessageString);
      } catch {
        setUploadError(errorMessageString);
      }
    } finally {
      setIsUploading(false);
    }
  };

  /**
* Passthrough handler for the modal's primary trigger to start uploads.
* 
* @param {File[]} files - The files provided by the uploader component.
*/
  const handleUpload = (files) => {
    handleRealFileUpload(files);
  };


  /**
 * Handles user logout, including manual and inactivity-triggered logouts.
 * Wrapped in `useCallback` to ensure a stable function reference for the inactivity timer.
 * This function calls the backend to invalidate the session, then resets the entire
 * React application state to its initial, logged-out condition. It provides different
 * toast messages to inform the user whether the logout was manual or due to a timeout.
 */
  const handleLogout = useCallback(async (isTimeout = false) => {
    if (!user) {
      console.warn("Logout called but no user was found.");
      return;
    }
    console.log("Logout initiated...");
    const apiTag = `POST ${API_LOGOUT_ENDPOINT}`;
    const requestPayload = { user_id: user.email };

    logger.info('Logout initiated.', { functionName: 'handleLogout', api: apiTag, requestPayload, isTimeout });
    setIsLoading(true);
    

    try {
      const response = await fetchWithIap(API_LOGOUT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Session-ID": sessionId,
          "Client-Type": 'Word',
        },
        body: JSON.stringify(requestPayload)
      });

      
      if (!response.ok) {
        let errorDetail = `Server responded with status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorDetail = errorData.error || errorDetail;
          logger.error('Logout API call failed.', { functionName: 'handleLogout', api: apiTag, status: response.status, errorPayload: errorData });
        } catch {
          console.error("Could not parse logout error response as JSON.");
        }
        throw new Error(errorDetail);
      }
      console.log("Backend session cleared successfully.");
      logger.info('User successfully logged out from backend.', { functionName: 'handleLogout', api: apiTag });
      setUser(null);
      setMessages(initialMessages);
      setActiveButton(null);
      setSessionId(crypto.randomUUID());
      setExistingFiles([]);
      setIsExistingFilesModalOpen(false);
      setDocLibFilters({ docTypes: [], customerAreas: [] });
      setIsPromptLibraryOpen(false);  
      // Show a different message depending on why the user was logged out.
      if (isTimeout) {
        toast.error("You have been logged out due to inactivity.");
      } else {
        toast.success("You have been successfully logged out.");
      }

    } catch (error) {
      logger.error('The logout process failed.', {
        functionName: 'handleLogout',
        api: apiTag,
        errorName: error.name,
        originalError: error
      });
      console.error("Logout process failed:", error);
      let userMessage;
      if (error.name === 'AbortError') {
        userMessage = "The logout request timed out. Please check your connection and try again.";
        logger.warn('Logout API call timed out.', { functionName: 'handleLogout', api: apiTag });
      } else {
        userMessage = `Logout failed: ${error.message}. Please try again.`;
      }
      userMessage = error.message || "Logout failed. Please try again.";
      toast.error(userMessage);
    } finally {
      
      setIsLoading(false);
    }
  }, [user, sessionId]);



  /**
* Dispatches a cancellation request to the backend to stop AI generation immediately.
* Modifies the UI to show the cancellation status dynamically.
*/
  const handleStopGenerating = useCallback(async () => {
    setIsStopping(true);
    const apiTag = `POST ${API_STOP_GENERATION_ENDPOINT}`;
    logger.info('User requested to stop generation. Sending signal to backend.', { functionName: 'handleStopGenerating', api: apiTag });

    try {
      const response = await fetchWithIap(API_STOP_GENERATION_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Session-ID": sessionId,
          "Client-Type": 'Word',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('Failed to send stop_generation signal.', { functionName: 'handleStopGenerating', api: apiTag, status: response.status, errorPayload: errorData });
        toast.error("Could not send stop signal. Aborting from client.");
        setIsStopping(false);
        // Fallback: If the API fails, abort the client-side request directly.
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
      } else {
        const jsonResponse = await response.json();
        logger.info('Successfully sent stop_generation signal. Backend will terminate the stream.', { functionName: 'handleStopGenerating', api: apiTag, response: jsonResponse });

        setMessages(prev => prev.map(m =>
          (m.isStreaming || m.type === 'loading-status' || m.type === 'typing')
            ? { ...m, type: 'loading-status', text: "Cancellation in progress..." }
            : m
        ));

      }
    } catch (error) {
      setIsStopping(false);
      logger.error('Exception while sending stop_generation signal.', { functionName: 'handleStopGenerating', api: apiTag, originalError: error });
      toast.error("An error occurred while trying to stop the generation.");
      // Fallback abort
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    }
  }, [sessionId]);

  /**
* Resubmits a previously failed user message request back to the generation pipeline.
* Performs the necessary guard checks to ensure context is still valid.
* 
* @param {string} messageId - The unique ID of the message to retry.
*/
  const handleRetry = async (messageId) => {
    // Validation: Check if the CURRENT source is DocLib and if filters are applied
    if (activeButton === 'DocLib') {
      const hasDocLibAccess = user?.roles?.some((role) =>
				docLibRequiredRoles.includes(role),
			);
	  if (!hasDocLibAccess) {
		setPermissionModal({ isOpen: true, featureName: "DocLib" });
		return;
	   }  
      const hasDocLibFilters = docLibFilters && (
        (docLibFilters.docTypes && docLibFilters.docTypes.length > 0) ||
        (docLibFilters.customerAreas && docLibFilters.customerAreas.length > 0)
      );

      if (!hasDocLibFilters) {
        toast.error("Please apply a filter to query DocLib.", {
          icon: '⚠️',
          style: {
            border: '1px solid #FFCC00',
            padding: '16px',
            color: '#333',
          },
        });
        return; // <--- ABORT RETRY
      }
    }

    // Validation: Check if the CURRENT source is File Upload and files are selected
    if (activeButton === 'file_upload') {
      const hasSelectedFiles = existingFiles.some(f => f.isSelected);
      if (!hasSelectedFiles) {
        toast.error("Please select at least one file from the list.");
        return; // <--- ABORT RETRY
      }
    }

    //  Find the message to retry
    const index = messages.findIndex((m) => m.id === messageId);
    const messageToRetry = messages[index];

    if (!messageToRetry) {
      console.error("Could not find message to retry");
      return;
    }

    //  Remove Error, Add Typing
    setMessages((prev) => {
      const newMessages = [...prev];

      //  Reset the error status on the user message
      newMessages[index] = { ...newMessages[index], errorStatus: null, dataSource: activeButton };

      // Check if the NEXT message is the bot error response and remove it
      // (We check specific types to ensure we don't delete wrong things)
      if (newMessages[index + 1] && (newMessages[index + 1].type === "error" || newMessages[index + 1].sender === "bot")) {
        newMessages.splice(index + 1, 1);
      }

      // Insert the "Typing" indicator at the exact same spot
      const typingMessage = { sender: "bot", type: "typing", avatar: botImg, id: "typing-" + messageId };
      newMessages.splice(index + 1, 0, typingMessage);

      return newMessages;
    });

    //  Re-run the message. 
    // PASS messageId as the last argument
    await handleSendMessage(
      messageToRetry.text,
      isGroundingEnabled,
      activeButton,
      true,
      messageId
    );
  };


  /**
* Sends a user's prompt to the backend and handles the resulting streaming response.
* Replaces typing indicators smoothly with incoming text and processes any structured Agent data formats.
* 
* @param {string} question - The user's query text.
* @param {boolean} isGroundingEnabled - Whether the backend should enforce grounded answers.
* @param {string|null} [sourceOverride=null] - Overrides standard data source behavior in retry contexts.
* @param {boolean} [isRetry=false] - Whether this constitutes a retry attempt of an existing prompt.
* @param {string|null} [existingMessageId=null] - Pre-existing message ID to link against if it's a retry.
*/
  const handleSendMessage = async (question, isGroundingEnabled, sourceOverride = null, isRetry = false, existingMessageId = null) => {
    const apiTag = `POST ${API_PROCESS_ENDPOINT}`;
    if (isLoading && !isRetry) return;
    if (!question.trim()) return;

    // Use the override if this is a retry, otherwise use current activeButton
    const currentSource = sourceOverride || activeButton;

    if (!user) {
      toast.info("You must be signed in to send a message.");
      return;
    }
    if (!currentSource) {
      toast.info("Please select a data source before asking a question.");
      return;
    }
      
    setError("");
    setIsLoading(true);
    setIsGenerating(true);

    // Initialize the new abort controller
    abortControllerRef.current = new AbortController();

    // We need a unique ID for the typing message to update it specifically
    const tempTypingId = "typing-" + (existingMessageId || crypto.randomUUID());

    // If retrying, use the passed ID. If new, create one.
    const currentMessageId = existingMessageId || crypto.randomUUID();

    if (!isRetry) {
      const userMessage = {
        sender: "user",
        text: question,
        user: user,
        id: currentMessageId,
        isBookmarked: false,
        isBookmarkLoading: false,
        dataSource: currentSource,
        errorStatus: null,
      };

      const typingMessage = { sender: "bot", type: "typing", avatar: botImg, id: tempTypingId };
      setMessages((prev) => [...prev, userMessage, typingMessage]);
    }

    const formData = new FormData();
    formData.append('query', question);
    formData.append('data', currentSource);
    formData.append('user_id', user.email);
    formData.append('grounding_enabled', isGroundingEnabled ? 'true' : 'false');  

    if (currentSource === 'file_upload') {
      const selectedFiles = existingFiles
        .filter(file => file.isSelected)
        .map(file => file.name);
      formData.append('selected_files', JSON.stringify(selectedFiles));
    }
    if (currentSource === 'DocLib') {
      const hasDocLibAccess = user?.roles?.some((role) =>
				docLibRequiredRoles.includes(role),
			);
	  if (!hasDocLibAccess) {
		toast.error("You do not have permission to access DocLib features.");
		setIsLoading(false);
		setIsGenerating(false);
		return;
	   }  
      formData.append('document_type', JSON.stringify(docLibFilters.docTypes));
      formData.append('customer_area', JSON.stringify(docLibFilters.customerAreas));
    }
    console.log("✅ Sending FormData to the backend. Query:", formData.get('query'), "Data:", formData.get('data'));

    // Used the helper function to create a loggable object from FormData
    const requestPayload = formDataToObject(formData);
    logger.info('Sending user query to process.', { functionName: 'handleSendMessage', api: apiTag, requestPayload });


    try {
      const response = await fetchWithIap(API_PROCESS_ENDPOINT, {
        method: "POST",
        headers: {
          "X-Session-ID": sessionId,
          "client-type": "Word",
        },
        body: formData,
        signal: abortControllerRef.current.signal,
      });

      console.log("response.status:", response.status)
      if (response.status === 504 || response.status === 429) {
        console.log("response.status:", response.status)
        logger.error(`Process message API failed with ${response.status}`, { functionName: 'handleSendMessage', api: apiTag });

        // Set a user-friendly message based on the status
        const errorMsg = response.status === 504
          ? "Request Timed Out: The server is taking too long to respond. Please try again."
          : "Too many requests. Please wait a moment and try again.";

        // Create the error and ATTACH THE STATUS
        const err = new Error(errorMsg);
        err.status = response.status;
        throw err;
      }
      if (!response.ok) {
        const errorText = await response.text();
        let displayMessage = `API error: ${response.status}`; // Default message

        try {
          const parsedJson = JSON.parse(errorText);
          if (parsedJson && parsedJson.error) {
            displayMessage = parsedJson.error;
          } else {
            displayMessage = errorText;
          }
        } catch  {
          displayMessage = errorText;
        }
        logger.error('Process message API returned an error.', { functionName: 'handleSendMessage', api: apiTag, status: response.status, errorPayload: errorText });
        const err = new Error(displayMessage);
        err.status = response.status;
        throw err;
      }

      if (!response.body) {
        throw new Error("Response has no body to read.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let fullResponseText = "";
      let isFirstChunk = true;
      let lastStatusMessage = "";
      //Process the streaming response
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        fullResponseText += chunk;
        // Don't do anything if the chunk is just whitespace or empty
        if (chunk.trim() === '') continue;

        // Check for Aiscribe#event: interrupted
        if (fullResponseText.includes("Aiscribe#event: interrupted")) {
          const eventMarker = "Aiscribe#event: interrupted";
          const eventIndex = fullResponseText.indexOf(eventMarker);
          const responseMarker = "Aiscribe#response:";

          // ADDED eventIndex to only look for the response AFTER the interrupted event
          const markerIndex = fullResponseText.indexOf(responseMarker, eventIndex);
          let interruptMessage = "This response was canceled and will not be included in your chat history.";

          if (markerIndex !== -1) {
            try {
               // eslint-disable-next-line no-unused-vars   
              const jsonString = fullResponseText.substring(markerIndex + responseMarker.length).trim();
            } catch {
              logger.warn('Could not parse JSON from interrupted event stream.', { functionName: 'handleSendMessage', rawText: fullResponseText });
            }
          }

          // Update the UI to show the cancellation message
          setMessages(prev =>
            prev.map(m =>
              (m.isStreaming === true || m.id === tempTypingId || m.type === 'loading-status')
                ? { ...m, isStreaming: false, type: 'error', text: interruptMessage, avatar: botImg }
                : m
            )
          );

          logger.info('Received interrupted event from stream. Stopping.', { functionName: 'handleSendMessage', message: interruptMessage });
          break; // Stop processing the stream
        }

        // Check for Aiscribe#event: fail
        if (fullResponseText.includes("Aiscribe#event: fail")) {
          const eventMarker = "Aiscribe#event: fail";
          const eventIndex = fullResponseText.indexOf(eventMarker);
          const responseMarker = "Aiscribe#response:";

          // ADDED eventIndex to only look for the response AFTER the fail event
          const markerIndex = fullResponseText.indexOf(responseMarker, eventIndex);
          let failMessage = "An error occurred while generating the response.";
          let failStatus = null;

          if (markerIndex !== -1) {
            const rawMsg = fullResponseText.substring(markerIndex + responseMarker.length).trim();
            try {
              const parsedJson = JSON.parse(rawMsg);
              failMessage = parsedJson.message || parsedJson.error || failMessage;
              failStatus = parsedJson.status_code || parsedJson.status || null;
            } catch {
              let finalMsg = rawMsg;
              if (finalMsg.startsWith('"') && finalMsg.endsWith('"')) {
                finalMsg = finalMsg.slice(1, -1);
              }
              failMessage = finalMsg;
            }
          }

          // Define which status codes should show the "Retry" button
          const retryableStatusCodes = [504, 503, 499, 429];

          setMessages(prev =>
            prev.map(m => {
              // If the status is retryable, update the user's message to show the button
              if (m.id === currentMessageId && retryableStatusCodes.includes(failStatus)) {
                return { ...m, errorStatus: failStatus };
              }
              // Update the bot's placeholder message to show the final error
              if (m.id === tempTypingId || m.isStreaming === true || m.type === 'loading-status' || m.type === 'typing') {
                return { ...m, isStreaming: false, type: 'error', text: failMessage, avatar: botImg };
              }
              return m;
            })
          );

          logger.warn('Received fail event from stream.', { functionName: 'handleSendMessage', failMessage });
          break; // Stop processing the stream
        }

        const statusRegex = /Aiscribe#event:\s*status[\r\n\s]+Aiscribe#response:\s*({[^\r\n]+})/g;
        const statusMatches = [...fullResponseText.matchAll(statusRegex)];

        if (statusMatches.length > 0) {
          const latestMatch = statusMatches[statusMatches.length - 1][1];
          try {
            const statusJson = JSON.parse(latestMatch);
            if (statusJson.message && statusJson.message !== lastStatusMessage) {
              lastStatusMessage = statusJson.message;

              // Update the UI with the dynamic backend text
              setMessages(prev => prev.map(m =>
                (m.id === tempTypingId && !m.isStreaming)
                  ? { ...m, type: "loading-status", text: lastStatusMessage }
                  : m
              ));
            }
          } catch (e) {
            console.error("error while status :", e)
          }
        }

        // Strip status events from the buffer so they don't render as chat text
        let cleanText = fullResponseText.replace(/Aiscribe#event:\s*status[\r\n\s]+Aiscribe#response:\s*{[^\r\n]+}\s*/g, '');

        const eventMarkerIndex = cleanText.indexOf('Aiscribe#event:');
        const currentText = eventMarkerIndex === -1 ? cleanText : cleanText.substring(0, eventMarkerIndex);

        if (isFirstChunk) {
          // Replace the "typing" indicator with the first chunk of text.
          if (currentText.trim() !== '') {
            isFirstChunk = false;
            setMessages(prev =>
              prev.map(m =>
                m.id === tempTypingId
                  ? {
                    ...m,
                    type: currentSource,
                    isStreaming: true,
                    text: currentText,
                  }
                  : m
              )
            );
          }
        } else {
          // Update the streaming message with new text.
          setMessages(prev =>
            prev.map(m =>
              m.isStreaming === true ? { ...m, text: currentText } : m
            )
          );
        }
      }

      //Finalize the response after the stream ends 
      // Only proceed if we didn't break early due to "Aiscribe#event: fail"
      if (!fullResponseText.includes("Aiscribe#event: fail") && !fullResponseText.includes("Aiscribe#event: interrupted")) {
        const successMarker = "Aiscribe#event: success";
        const successIndex = fullResponseText.indexOf(successMarker);

        if (successIndex !== -1) {
          const responseMarker = "Aiscribe#response:";
          const jsonStartIndex = fullResponseText.indexOf(responseMarker, successIndex);
          if (jsonStartIndex !== -1) {
            const jsonString = fullResponseText.substring(jsonStartIndex + responseMarker.length).trim();
            try {
              const finalJson = JSON.parse(jsonString);
              logger.info('Successfully parsed streamed response.', { functionName: 'handleSendMessage', api: apiTag, responsePayload: finalJson });
              const receivedRequestLogId = finalJson.request_log_id;
              let finalBotResponse;

              const sqlAgentMarker = '$$SQL AGENT$$.';
              const messageText = finalJson.messages || "";
                
            
              // Special handling for responses from the SQL Agent
             if (messageText.startsWith(sqlAgentMarker)) {
                logger.info('SQL Agent response detected.', { functionName: 'handleSendMessage' });

                const jsonArrayStartIndex = messageText.indexOf('[');

                if (jsonArrayStartIndex !== -1) {
                  const introText = messageText.substring(sqlAgentMarker.length, jsonArrayStartIndex).trim();
                  const tableJsonString = messageText.substring(jsonArrayStartIndex);

                  try {
                    const tableData = JSON.parse(tableJsonString);
                    finalBotResponse = {
                      sender: "bot",
                      type: "sql-table", // *** This is the key! ***
                      introText: introText,
                      tableData: tableData,
                      sqlFileUrls: finalJson.sql_file_urls || [],
                      avatar: botImg,
                      fileNames: finalJson.file_names || [],
                      urls: finalJson.urls || [],
                      request_log_id: receivedRequestLogId,
                    };
                  } catch (e) {
                    logger.error('Failed to parse SQL Agent JSON data.', { functionName: 'handleSendMessage', error: e, rawText: tableJsonString });
                    finalBotResponse = { sender: "bot", type: "error", text: "Failed to parse table data from response.", avatar: botImg, request_log_id: receivedRequestLogId };
                  }
                } else {
                  logger.warn('SQL Agent marker found, but no JSON array start bracket.', { functionName: 'handleSendMessage', rawText: messageText });
                  finalBotResponse = { sender: "bot", type: activeButton, text: messageText, avatar: botImg, request_log_id: receivedRequestLogId };
                }
              }
                else {
                finalBotResponse = {
                  sender: "bot",
                  type: activeButton,
                  text: messageText || "Received an empty response.",
                  avatar: botImg,
                  carouselLinks: finalJson.carousel_links || [],
                  fileNames: finalJson.file_names || [],
                  urls: finalJson.urls || [],
                  pageNumbers: finalJson.file_page_nos || [],
                  request_log_id: receivedRequestLogId,
                };
              }

              setMessages(prev =>
                prev.map(m => {
                  if (m.id === currentMessageId) {
                    return { ...m, request_log_id: receivedRequestLogId };
                  }
                  if (m.id === tempTypingId || m.isStreaming === true) {
                  return { ...finalBotResponse, id: tempTypingId }; 
                 }
                  return m;
                })
              );

            } catch (e) {
              // Catch parsing error of the final 'Aiscribe#response:' JSON
              console.error("Failed to parse final JSON object from stream.:",e)
              logger.error('Failed to parse final JSON object from stream.', { functionName: 'handleSendMessage', error: e, rawText: jsonString });
              const errorObject = {
                sender: "bot",
                type: "error",
                text: "We apologize, but we couldn't load the final details of your request. Please try again in a moment.",
                avatar: botImg,
                isStreaming: false
              };
              setMessages((prev) =>
                prev.map(m =>
                  m.isStreaming === true ? errorObject : m
                )
              );

            }
          }
        } else {
        
          // Strip all status events from the fallback text so they don't show in UI
          let fallbackText = fullResponseText.replace(/Aiscribe#event:\s*status[\s\S]*?Aiscribe#response:\s*{[^\r\n]+}\s*/g, '');

          // Strip any other lingering Aiscribe tags just to be completely safe
          fallbackText = fallbackText.replace(/Aiscribe#(event|response):[^\r\n]*/g, '').trim();

          //Create the small note to append
          const interruptedNote = `<div class="interrupted-note">⚠️ *The request took too long and couldn't be fully completed. It looks like you're asking for a lot of data at once! Please try again with a more specific request to see the complete results.*.</div>`;

          // 4. If we got some partial text, append the note. If we got nothing, show just the error.
          if (!fallbackText) {
            fallbackText = "The response stream finished unexpectedly. It looks like you're asking for a lot of data at once! Please try again with a more specific request to see the complete results.";
          } else {
            fallbackText = fallbackText + interruptedNote;
          }

          setMessages(prev =>
            prev.map(m =>
              (m.id === tempTypingId || m.isStreaming === true) ? { ...m, isStreaming: false, type: activeButton, text: fallbackText } : m
            )
          );
          console.warn("Stream finished but 'Aiscribe#response:' marker not found.");
          logger.warn("Stream finished but 'Aiscribe#response:' marker not found.", { functionName: 'handleSendMessage' });
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        logger.info('Generation aborted by user.', { functionName: 'handleSendMessage' });
        setMessages((prev) => prev.map(m =>
          (m.isStreaming === true || m.type === 'typing')
            ? { ...m, isStreaming: false, type: 'error', text: "This response was canceled and will not be included in your chat history.", avatar: botImg }
            : m
        ));
        return; // Exit out gracefully
      }
      console.error("Failed to fetch API:", error);
      console.error("Failed to fetch API error status::", error.status)

      const displayMessage = error.message || "Sorry, something went wrong. Please try again.";


      setMessages((prev) =>
        prev.map((m) => {
          //  Tag the user message with the error code so the Retry button appears
          if (m.id === currentMessageId && (error.status === 429 || error.status === 503 || error.status === 504 || error.status === 499)) {
            return { ...m, errorStatus: error.status };
          }

          // Turn the "Typing/Thinking" message into the actual Error message
          if (m.id === tempTypingId) {
            return {
              ...m,
              sender: "bot",
              type: "error",
              text: displayMessage,
              isStreaming: false,
              avatar: botImg,
              errorStatus: error.status
            };
          }
          return m;
        })
      );
    } finally {
      setIsLoading(false);
      setIsGenerating(false);
      setIsStopping(false);
    }
    console.log(`Sending message with source: ${activeButton}`);
  };

  
  /**
   * Clears the current conversation and starts a new session.
   * This function calls a backend endpoint to clear the server-side conversation history.
   * Crucially, it then generates a new `sessionId` on the client. This ensures that
   * subsequent messages belong to a fresh, distinct conversation. Finally, it resets the
   * UI by clearing messages and de-selecting any active data source.
   */
  const handleClearChat = async () => {
    const apiTag = `POST ${API_LOGOUT_ENDPOINT}`;
    if (isLoading) return;
    logger.info('Clear chat initiated.', { functionName: 'handleClearChat', api: apiTag });
    setIsLoading(true);
    const formData = new FormData();
    formData.append("user_id", user.email);

    try {
      const response = await fetchWithIap(API_LOGOUT_ENDPOINT, {
        method: "POST",
        headers: {
          "X-Session-ID": sessionId,
          "Client-Type": 'Word',
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // If it's just "no history", that's not a true error.
        if (errorData.message.includes("No conversation history")) {
          logger.info('Chat clear requested, but no history existed on backend.', { functionName: 'handleClearChat', api: apiTag });
          toast.success("Chat was already clear.");
        } else {
          logger.error('Clear chat API call failed.', { functionName: 'handleClearChat', api: apiTag, status: response.status, errorPayload: errorData });
          throw new Error(errorData.error || `Server error: ${response.status}`);

        }
      }

      // Generate a new session ID for the new, fresh session.
      const newSessionId = crypto.randomUUID();
      setSessionId(newSessionId);
      console.log("Chat cleared. New session ID created:", newSessionId);
      logger.info('Chat cleared and new session started.', { functionName: 'handleClearChat', oldSessionId: sessionId, newSessionId: newSessionId });

      // Reset the chat messages in the UI to the welcome message.
      const userName = user.displayName || "User";
      const personalizedWelcomeMessage = {
        sender: "bot",
        type: "welcome",
        text: `Hello ${userName}, I am AIScribe. How can I assist you today?`,
        avatar: botImg,
      };
      setMessages([personalizedWelcomeMessage]);
      setActiveButton(null);
      setExistingFiles([]);
      setIsExistingFilesModalOpen(false);
      setDocLibFilters({ docTypes: [], customerAreas: [] });
      toast.success("Chat cleared and a new session has started.");
    } catch (error) {
      console.error("Failed to clear session:", error);
      logger.error('The clear chat process failed.', {
        functionName: 'handleClearChat',
        api: apiTag,
        originalError: error
      });
      toast.error(error.message);
    } finally {
      setIsLoading(false); // Re-enable UI elements.
    }
  };

  /**
 * Simple handler to surface application version or info.
 */
  const handleShowInfo = () => {
    toast(
      "AIScribe v1.0\nYour intelligent assistant for document and data queries.");
    console.log("INFO button was clicked!");
  };

  /**
   * Opens the Prompt Library in a new browser window.
   * This handler sets a state variable that conditionally renders the `NewWindowPortal`
   * component, which is responsible for creating and managing the separate window
   * and rendering the `PromptLibrary` component inside of it.
   */
  const handleShowPromptLibrary = () => {
    logger.info('User is opening the prompt library window.', { functionName: 'handleShowPromptLibrary' });
    setIsPromptLibraryOpen(true);
  };
  /**
   * Handles closing the Prompt Library window.
   * Wrapped in `useCallback` for stability when passed as a prop. This function sets
   * state to `false` to unmount the portal component. It also increments a `toasterKey`,
   * a clever workaround to force the `Toaster` component to re-render and ensure
   * that toasts will function correctly in the main window after the portal is closed.
   */
  const handleClosePromptLibrary = useCallback(() => {
    setIsPromptLibraryOpen(false);
    setToasterKey(prevKey => prevKey + 1);
  }, []);

  /**
  * Submits user feedback (like/dislike) for a specific bot response.
  * This is designed as a non-blocking, "fire-and-forget" background task. It sends
  * the feedback payload to the backend. If the feedback was positive (`rating: 1`),
  * it shows a simple thank you toast. The flow for negative feedback (which opens
  * a detailed comment modal) is handled separately.
  */
  const handleFeedbackSubmit = async (feedbackPayload) => {
    const apiTag = 'POST /feedback';
    logger.info('Submitting feedback.', { functionName: 'handleFeedbackSubmit', api: apiTag, requestPayload: feedbackPayload });
    try {
      const response = await fetchWithIap(API_FEEDBACK_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'Client-Type': 'Word',
        },
        body: JSON.stringify(feedbackPayload),
      });

      if (!response.ok) {
        const errorJson = await response.json().catch(() => ({}));
        logger.error('Feedback submission failed.', { functionName: 'handleFeedbackSubmit', api: apiTag, status: response.status, errorPayload: errorJson });
        throw new Error(errorJson.error || `Feedback submission failed: ${response.status}`);
      }
      logger.info('Feedback submitted successfully.', { functionName: 'handleFeedbackSubmit', api: apiTag, status: response.status });
      if (feedbackPayload.rating === 1) {
        toast.success("Thanks for your feedback!");
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error(error.message);
    }
  };

  /**
 * @description Logs the "copy to word" or "insert to word" action by sending the request_log_id to the backend.
 * This is a fire-and-forget call; it logs an error on failure but does not block the user.
 * @param {string} requestLogId - The unique ID for the request-response pair being copied/inserted.
 * @returns {Promise<boolean>} A promise that resolves to true if logging was successful, false otherwise.
 */
  const logCopyInteraction = async (requestLogId) => {
    if (!requestLogId) {
      logger.warn('logCopyInteraction called without a requestLogId.');
      console.warn("logCopyInteraction called without a requestLogId. Skipping log.");
      return false;
    }

    const apiTag = `POST ${API_RESPONSE_COPY_ENDPOINT}`;
    const requestPayload = { "request_log_id": requestLogId };

    logger.info("Attempting to log 'copy/insert' interaction.", { functionName: 'logCopyInteraction', api: apiTag, requestPayload });

    try {
      const logResponse = await fetchWithIap(API_RESPONSE_COPY_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'Client-Type': 'Word',
        },
        body: JSON.stringify(requestPayload)
      });

      if (!logResponse.ok) {

        let errorPayload = {};
        try {
          errorPayload = await logResponse.json();
        } catch {
          const rawText = await logResponse.text().catch(() => 'Could not read or parse error response.');
          errorPayload = { error: 'The API error response was not in the expected JSON format.', rawResponse: rawText };
          console.error("logCopyInteraction: The API error response was not valid JSON.", rawText);
        }
        logger.error("Failed to log 'copy/insert' interaction.", {
          functionName: 'logCopyInteraction',
          api: apiTag,
          status: logResponse.status,
          errorPayload: errorPayload,
          context: { requestPayload }
        });
        console.error(`Failed to log 'copy/insert' interaction. Status: ${logResponse.status}`, errorPayload);
        return false;
      }
      const jsonResponse = await logResponse.json();
      console.log("Successfully logged 'copy/insert' interaction.");
      logger.info("Successfully logged 'copy/insert' interaction.", {
        functionName: 'logCopyInteraction',
        api: apiTag,
        context: {
          requestPayload: requestPayload,
          responsePayload: jsonResponse
        }
      });
      return true;

    } catch (error) {
      logger.error("Exception during 'copy/insert' logging.", {
        functionName: 'logCopyInteraction',
        api: apiTag,
        errorMessage: error.message,
        originalError: error,
        context: { requestPayload }
      });
      console.error("Error during the copy logging API call:", error);
      return false;
    }
  };


  /**
* Action handler executed automatically when the overarching application inactivity timer expires.
* Wrapped in `useCallback` to prevent continuous timer resets based on function reference updates.
*/
  const handleInactivity = useCallback(() => {
    logger.warn('User is being logged out due to inactivity.');
    handleLogout(true);
  }, [handleLogout]);

  /**
 * Custom hook monitoring global user action inputs to trigger time-out limits.
 */
  useInactivityTimer({
    isLoggedIn: !!user,
    timeout: inactivityTimeout,
    onTimeout: handleInactivity,
  });

  /**
* Toggles whether a specific user prompt is saved ("bookmarked") into the Prompt Library.
* Performs an optimistic loading mutation, followed by an API call and success stabilization.
* 
* @param {string} requestLogId - The backend ID for the user's prompt interaction cycle.
*/
  const handleToggleBookmark = async (requestLogId) => {
    const updateSelector = (msg) => msg.sender === 'user' && msg.request_log_id === requestLogId;
    const messageToUpdate = messages.find(updateSelector);

    if (!messageToUpdate) {
      console.error("Could not find message to bookmark with id:", requestLogId);
      return;
    }
    if (messageToUpdate.isBookmarkLoading) {
      console.warn("Bookmark action already in progress for this message.");
      return;
    }

    const isAddingBookmark = !messageToUpdate.isBookmarked;

    setMessages(prev =>
      prev.map(msg =>
        updateSelector(msg) ? { ...msg, isBookmarkLoading: true } : msg
      )
    );

    let endpoint, method, payload, apiTag, successMessage, loggerMessage;

    if (isAddingBookmark) {
      endpoint = API_ADD_BOOKMARK_ENDPOINT;
      method = 'POST';
      payload = { request_log_id: requestLogId };
      apiTag = `POST ${endpoint}`;
      successMessage = '🔖 Prompt Bookmarked! It\'s now saved in Prompt Library.';
      loggerMessage = 'Prompt Bookmarked!';
    } else {
      endpoint = API_REMOVE_BOOKMARKS_ENDPOINT;
      method = 'DELETE';
      payload = { request_log_ids: [requestLogId] }; // Backend expects an array
      apiTag = `DELETE ${endpoint}`;
      successMessage = '🔖 Bookmark Removed. This prompt is no longer in Prompt Library.';
      loggerMessage = 'Bookmark Removed.';
    }

    logger.info(`Attempting to ${isAddingBookmark ? 'add' : 'remove'} bookmark.`, { functionName: 'handleToggleBookmark', api: apiTag, payload });

    try {
      const response = await fetchWithIap(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'Client-Type': 'Word',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorJson = await response.json().catch(() => ({}));
        const errorMessage = errorJson.error || `Failed to update bookmark. Status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const jsonResponse = await response.json();
      setMessages(prev =>
        prev.map(msg =>
          updateSelector(msg)
            ? { ...msg, isBookmarked: isAddingBookmark, isBookmarkLoading: false }
            : msg
        )
      );
      // If the prompt library is open as a React component, we refresh it by changing its key
      if (isPromptLibraryOpen) {
        setPromptLibraryRefreshKey(prevKey => prevKey + 1);
        logger.info('Triggering Prompt Library refresh via key change.', { functionName: 'handleToggleBookmark' });
      }

      setTimeout(() => {
        toast.success(successMessage, { containerId: 'aiscribe-main' });
      }, 100);

      logger.info(loggerMessage, {
        functionName: 'handleToggleBookmark',
        api: apiTag,
        context: { requestPayload: payload, responsePayload: jsonResponse }
      });
    } catch (error) {

      logger.error(`Failed to ${isAddingBookmark ? 'add' : 'remove'} bookmark. Rolling back UI.`, {
        functionName: 'handleToggleBookmark',
        originalError: error,
        api: apiTag,
        context: { requestPayload: payload }
      });
      toast.error(error.message);
      setMessages(prev =>
        prev.map(msg =>
          updateSelector(msg)
            ? { ...msg, isBookmarkLoading: false }
            : msg
        )
      );
    }
  };

  /**
 * Update handler reflecting a deletion made from within the Prompt Library portal.
 * Modifies the UI of the central application correctly to unsave the bookmark inline.
 * 
 * @param {string} deletedLogId - The ID of the item just deleted from the library.
 */
  const handleBookmarkDeleted = (deletedLogId) => {
    logger.info(`Received deletion event for bookmark ID ${deletedLogId} from library.`, { functionName: 'handleBookmarkDeleted' });
    setMessages(prevMessages =>
      prevMessages.map(msg =>
        (msg.id === deletedLogId || msg.request_log_id === deletedLogId) // Check both just in case
          ? { ...msg, isBookmarked: false, isBookmarkLoading: false }
          : msg
      )
    );
  };

  /**
* Action handler to strip an individual category filter from the 'DocLib' source type arrays.
* 
* @param {string} category - The namespace key the filter originates from (e.g. 'docTypes').
* @param {string} value - The precise text value to filter out.
*/
  const handleRemoveDocLibFilter = (category, value) => {
    console.log('handleRemoveDocLibFilter called')

    setDocLibFilters((prev) => {
      const newFilters = {
        ...prev,
        [category]: prev[category].filter((item) => item !== value),
      };
      return newFilters;
    });
  };

  /**
 * Primary acceptance layer to lock in multi-filter configurations generated in a popover.
 * 
 * @param {Object} filters - Dictionary structure containing newly confirmed configuration arrays.
 * @param {boolean} [showToast=true] - Whether to audibly confirm the active filters to the user.
 */
  const handleDocLibApply = (filters, showToast = true) => {
    // Access control validation check
	const hasDocLibAccess = user?.roles?.some((role) =>
		docLibRequiredRoles.includes(role),
	);
	if (!hasDocLibAccess) {
		setPermissionModal({ isOpen: true, featureName: "DocLib" });
		return; // Stop applying actions
	}  
    setDocLibFilters(filters);
    setActiveButton("DocLib");
    setError("");
    if (showToast && filters.docTypes.length > 0 && filters.customerAreas.length > 0) {
      toast.success(`DocLib active: ${filters.docTypes.length} types, ${filters.customerAreas.length} areas selected.`);
    }
  };
    
  return (
    <>
      <Toaster
        key={toasterKey}
        containerId="aiscribe-main"
        container={window.document.body}
        position="top-center"
        reverseOrder={false}
        containerStyle={{
          zIndex: 9999999,
        }}
        toastOptions={{
          duration: 5000,
          error: {
            duration: 6000,
            style: {
              background: '#fde7e9',
              color: '#d13438',
            },
          },
          success: {
            duration: 5000,
            style: {
              background: '#dff6dd',
              color: '#107c10'
            }
          }
        }}
      />
      {/* Now, we conditionally render the rest of the UI */}
      {authIsLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <Spinner label="Initializing..." />
        </div>
      ) : !user ? (
        // If not logged in, show the LoginPage
        <LoginPage onLogin={onLoginRequest} isLoading={isAuthenticating} error={authError || error} />
      ) : (
        // If logged in, show the main application interface
        <div>
          <ChatInterface
            title={title}
            onSourceChange={handleSourceChange}
            activeButton={activeButton}
            user={user}
            onLogout={handleOpenLogoutModal}
            messages={messages}
            onSendMessage={handleSendMessage}
            error={error}
            isLoading={isLoading}
            onUploadClick={handleUploadClickFlow}
            isCheckingFiles={isCheckingFiles}
            onClearChat={handleOpenClearChatModal}
            onShowInfo={handleShowInfo}
            onShowPromptLibrary={handleShowPromptLibrary}
            existingFiles={existingFiles}
            onToggleFileSelection={handleToggleFileSelection}
            onSelectAll={handleSelectAllFiles}
            onFeedbackSubmit={handleFeedbackSubmit}
            sessionId={sessionId}
            onLogCopy={logCopyInteraction}
            onToggleBookmark={handleToggleBookmark}
            isUploading={isUploading}
            showRevaluationWarning={showRevaluationWarning}
            onDismissRevaluationWarning={handleDismissRevaluationWarning}
            docLibFilters={docLibFilters}
            onDocLibApply={handleDocLibApply}
            onRemoveDocLibFilter={handleRemoveDocLibFilter}
            onRetry={handleRetry}
            isGroundingEnabled={isGroundingEnabled}
            onGroundingChange={setIsGroundingEnabled}
            isGenerating={isGenerating}
            onStopGenerating={handleStopGenerating}
            isStopping={isStopping}
            romineRequiredRole={romineRequiredRole}
            docLibRequiredRoles={docLibRequiredRoles}
          />
          <UploadModal
            isOpen={isUploadModalOpen}
            onClose={handleCloseUploadModal}
            onUpload={handleUpload}
            isUploading={isUploading}
            uploadError={uploadError}
            onClearUploadError={() => setUploadError("")}
            uploadQuotaData={uploadQuotaData}
            onClearQuotaData={() => setUploadQuotaData(null)}
            onBackgroundCleanup={handleDeleteSingleExistingFile}
            existingFileCount={existingFiles.length}
            existingFilesSize={totalExistingFileSize}
            existingFileNames={existingFileNames}
            maxTokenLimit={maxTokenLimit}
            totalToken={totalToken}
            onManageExistingFiles={handleManageExistingFiles}
            existingFiles={existingFiles}
            onDeleteExistingFile={handleDeleteSingleExistingFile}
          />
          {/* Render the new modal */}
          <ExistingFilesModal
            isOpen={isExistingFilesModalOpen}
            onClose={handleCloseExistingFilesModal}
            files={existingFiles}
            onUploadNew={handleProceedToUpload}
            onToggleFileSelection={handleToggleFileSelection}
            onSelectAll={handleSelectAllFiles}
            onDelete={handleUnifiedDelete}
            isDeleting={isDeletingFiles}
            totalToken={totalToken}
            maxTokenLimit={maxTokenLimit}
          />
          {/* REUSABLE ACCESS DENIED MODAL */}
		  <PermissionModal
			isOpen={permissionModal.isOpen}
			featureName={permissionModal.featureName}
            userRoles={user?.roles}
			onClose={() =>
			 setPermissionModal({ isOpen: false, featureName: "" })
			}
		   />        
          {/* RENDER THE GENERIC MODAL using the config state */}
          {modalConfig && (
            <ConfirmationModal
              isOpen={!!modalConfig}
              onClose={handleCloseModal}
              onConfirm={handleConfirmModal}
              isLoading={isLoading}
              title={modalConfig.title}
              message={modalConfig.message}
              confirmButtonText={modalConfig.confirmButtonText}
            />
          )}
          {/* FAILED FILES SUMMARY MODAL */}
             <Dialog open={isFailedFilesModalOpen} onOpenChange={(event, data) => !data.open && setIsFailedFilesModalOpen(false)}>
              <DialogSurface style={{ padding: "10px", width: "90vw", maxWidth: "550px" }}>
               <DialogBody>
                <DialogTitle style={{ fontSize: "16px", color: "#d13438", display: "flex", alignItems: "center", gap: "8px" }}>
                 ⚠️ Upload Failures
                </DialogTitle>
                <DialogContent>
                 <Text style={{ marginBottom: "12px", display: "block", fontSize: "13px", color: "#323130" }}>
                  The following files encountered errors during processing. Please review the details below:
                 </Text>
                 <ul style={{
                  maxHeight: "250px",
                  overflowY: "auto",
                  padding: "0",
                  margin: "0",
                  listStyleType: "none",
                  border: "1px solid #fde7e9",
                  borderRadius: "4px"
                 }}>
                  {failedFilesErrors.map((fileInfo, idx) => (
                   <li key={idx} style={{
                    padding: "10px",
                    backgroundColor: idx % 2 === 0 ? "#fff" : "#fdf3f4",
                    borderBottom: idx < failedFilesErrors.length - 1 ? "1px solid #fde7e9" : "none"
                   }}>
                    <Text weight="semibold" style={{ color: "#d13438", display: "block", fontSize: "12px", wordBreak: "break-all" }}>
                     {/* Uses the filename if available from the backend, otherwise defaults to sequence */}
                     {fileInfo.filename || fileInfo.name || fileInfo.file || `File ${idx + 1}`}
                    </Text>
                    <Text style={{ fontSize: "11px", color: "#605e5c", marginTop: "2px", display: "block" }}>
                     {fileInfo.error || "An unknown error occurred during conversion."}
                    </Text>
                   </li>
                  ))}
                 </ul>
                </DialogContent>
                <DialogActions style={{ paddingTop: "16px" }}>
                 <Button appearance="primary" onClick={() => setIsFailedFilesModalOpen(false)}>
                  Close
                 </Button>
                </DialogActions>
               </DialogBody>
              </DialogSurface>
             </Dialog>
                  
                  
          {isPromptLibraryOpen && (
            <NewWindowPortal onClose={handleClosePromptLibrary}>
              <PromptLibrary
                user={user}
                sessionId={sessionId}
                refreshKey={promptLibraryRefreshKey}
                onBookmarkDeleted={handleBookmarkDeleted}
              />
            </NewWindowPortal>
          )}
          {isRefreshingSession && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 9999999,
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              backdropFilter: 'blur(5px)',
              WebkitBackdropFilter: 'blur(5px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {/* The White Message Box */}
              <div style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '8px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                maxWidth: '400px',
                textAlign: 'center',
                border: '1px solid #e0e0e0'
              }}>
                {/* Spinner */}
                <div style={{ marginBottom: '20px' }}>
                  <Spinner size="large" />
                </div>

                <h3 style={{ margin: '0 0 10px 0', color: '#323130', fontFamily: 'Segoe UI' }}>
                  Authentication in Progress
                </h3>

                <p style={{ margin: '0 0 15px 0', color: '#605e5c', fontSize: '14px', lineHeight: '1.5' }}>
                  Your secure session has expired. A popup window has opened to refresh your credentials.
                </p>

                <div style={{
                  backgroundColor: '#fff4ce',
                  color: '#4a4028',
                  padding: '10px',
                  fontSize: '13px',
                  borderRadius: '4px',
                  borderLeft: '4px solid #fce100'
                }}>
                  <strong>⚠️ Please do not close the popup window.</strong><br />
                  It will close automatically once finished.
                </div>
              </div>
            </div>
          )}

        </div>
      )}
    </>
  );
};

App.propTypes = {
  title: PropTypes.string,
  onLoginRequest: PropTypes.func,
  initialProxySessionId: PropTypes.string,
  isAuthenticating: PropTypes.bool,
  authError: PropTypes.string,
};

App.defaultProps = {
  title: '',
  onLoginRequest: undefined,
  initialProxySessionId: null,
  isAuthenticating: false,
  authError: null,
};

export default App;